import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import {
  useSettingsPatientDocuments,
  useIntakeForm,
  useIntakeFormPreview,
  useUpdateIntakeForm,
} from '@/v2/hooks/settings/useSettingsPatientDocuments'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import FieldLabel from '@/v2/components/common/FieldLabel'
import Typography from '@/v2/components/common/Typography'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import useFormModal from '@/v2/hooks/settings/useFormModal'
import {
  SettingsPage,
  CardGrid,
  Card,
  CardTitle,
} from '@/v2/components/settings/shared'

const DocumentStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
`

const StatusIcon = styled.div`
  width: 1rem;
  height: 1rem;
  color: ${props => (props.active ? '#10b981' : '#dc2626')};
`

const LinkFieldsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`

const PreviewButton = styled.button`
  background: none;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #eff6ff;
  }
`

const SignatureLine = styled.div`
  border-top: 1px solid #9ca3af;
  width: 60%;
  margin-top: 2rem;
  padding-top: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
`

const INITIAL_FORM = {
  title: '',
  introduction: '',
  conditions: '',
  signatureEnabled: true,
  active: false,
  termsEnabled: false,
  linkTitle: '',
  linkUrl: '',
  autoSend: false,
}

const PatientDocuments = () => {
  const { documents: rawDocuments, isLoading } = useSettingsPatientDocuments()
  const { intakeForm, refetch: refetchIntakeForm } = useIntakeForm()
  const { updateIntakeForm, isPending: isSaving } = useUpdateIntakeForm()
  const {
    previewData,
    isLoading: isPreviewLoading,
    refetch: refetchPreview,
  } = useIntakeFormPreview()
  const { onOpenSnackbar } = useSnackbarContext()

  const [showPreviewModal, setShowPreviewModal] = useState(false)

  const {
    showModal,
    editingItem,
    formData,
    isEditing,
    openForEdit,
    close,
    updateField,
  } = useFormModal(INITIAL_FORM)

  const documents = useMemo(() => {
    if (!rawDocuments) return []
    const docs = []
    if (rawDocuments.intakeForm) {
      docs.push({
        id: 'intake_form',
        type: 'intake_form',
        title: rawDocuments.intakeForm.title || 'Intake Form',
        active: rawDocuments.intakeForm.active || false,
        autoSend: rawDocuments.intakeForm.autoSend || false,
      })
    }
    return docs
  }, [rawDocuments])

  const handleEdit = async document => {
    try {
      const { data } = await refetchIntakeForm()
      openForEdit(document, () => ({
        title: data?.title || '',
        introduction: data?.introduction || '',
        conditions: data?.conditions || '',
        signatureEnabled: data?.signatureEnabled || false,
        active: data?.active || false,
        termsEnabled: data?.termsEnabled || false,
        linkTitle: data?.linkTitle || '',
        linkUrl: data?.linkUrl || '',
        autoSend: data?.autoSend || false,
      }))
    } catch (error) {
      onOpenSnackbar('Failed to load intake form details', 'error')
      openForEdit(document, () => ({
        title: document.title || '',
        introduction: '',
        conditions: '',
        signatureEnabled: false,
        active: document.active || false,
        termsEnabled: false,
        linkTitle: '',
        linkUrl: '',
        autoSend: false,
      }))
    }
  }

  const handleSubmit = async () => {
    try {
      await updateIntakeForm({
        title: formData.title,
        introduction: formData.introduction,
        conditions: formData.conditions,
        signature_enabled: formData.signatureEnabled,
        active: formData.active,
        terms_enabled: formData.termsEnabled,
        link_title: formData.linkTitle,
        link_url: formData.linkUrl,
        auto_send: formData.autoSend,
      })
      onOpenSnackbar('Intake form updated successfully', 'success')
      close()
    } catch (error) {
      onOpenSnackbar('Failed to update intake form', 'error')
    }
  }

  const handlePreview = async e => {
    if (e) e.stopPropagation()
    try {
      await refetchPreview()
      setShowPreviewModal(true)
    } catch (error) {
      onOpenSnackbar('Failed to load intake form preview', 'error')
    }
  }

  const getStatusLabel = document => {
    if (!document.active) return 'Inactive'
    if (document.autoSend) return 'Auto-send enabled'
    return 'Manual send'
  }

  return (
    <SettingsPage
      title="Patient Documents"
      subtitle="Configure intake forms, consent forms, and document templates"
      loading={isLoading}
    >
      <CardGrid>
        {documents.map(document => (
          <Card key={document.id} onClick={() => handleEdit(document)}>
            <CardTitle $mb="0.75rem">{document.title}</CardTitle>
            <DocumentStatus>
              <StatusIcon active={document.active}>
                {document.active ? (
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                )}
              </StatusIcon>
              {getStatusLabel(document)}
            </DocumentStatus>
            <Box sx={{ mt: '0.75rem' }}>
              <PreviewButton onClick={handlePreview}>Preview</PreviewButton>
            </Box>
          </Card>
        ))}
      </CardGrid>

      <Modal
        isOpen={showModal && !!editingItem}
        title={editingItem ? `Edit ${editingItem.title}` : ''}
        onClose={close}
        onConfirm={handleSubmit}
        confirmLabel="Save Changes"
        cancelLabel="Cancel"
        submitDisabled={isSaving}
        loading={false}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <FieldLabel>Basic Information</FieldLabel>

          <TextField
            fullWidth
            label="Form Title"
            placeholder="Enter form title"
            value={formData.title}
            onChange={val => updateField('title', val)}
          />

          <TextField
            fullWidth
            label="Introduction Message"
            placeholder="Message shown to patients at the beginning of the form"
            value={formData.introduction}
            onChange={val => updateField('introduction', val)}
            multiline
            rows={4}
          />

          <TextField
            fullWidth
            label="Terms and Conditions"
            placeholder="Legal terms and conditions text"
            value={formData.conditions}
            onChange={val => updateField('conditions', val)}
            multiline
            rows={4}
          />

          <Box sx={{ mt: '8px' }}>
            <FieldLabel>Form Settings</FieldLabel>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                mt: '8px',
              }}
            >
              <CheckboxField
                label="Enable this form for patients"
                checked={formData.active}
                onChange={e => updateField('active', e.target.checked)}
              />

              <CheckboxField
                label="Automatically send to new patients"
                checked={formData.autoSend}
                onChange={e => updateField('autoSend', e.target.checked)}
              />

              <CheckboxField
                label="Require digital signature"
                checked={formData.signatureEnabled}
                onChange={e =>
                  updateField('signatureEnabled', e.target.checked)
                }
              />

              <CheckboxField
                label="Include terms and conditions link"
                checked={formData.termsEnabled}
                onChange={e => updateField('termsEnabled', e.target.checked)}
              />
            </Box>
          </Box>

          {formData.termsEnabled && (
            <Box sx={{ mt: '8px' }}>
              <FieldLabel>Terms & Conditions Link</FieldLabel>
              <LinkFieldsRow style={{ marginTop: '8px' }}>
                <TextField
                  fullWidth
                  label="Link Text"
                  placeholder="Privacy Policy"
                  value={formData.linkTitle}
                  onChange={val => updateField('linkTitle', val)}
                />
                <TextField
                  fullWidth
                  label="Link URL"
                  placeholder="https://example.com/privacy"
                  value={formData.linkUrl}
                  onChange={val => updateField('linkUrl', val)}
                  type="url"
                />
              </LinkFieldsRow>
            </Box>
          )}
        </Box>
      </Modal>

      <Modal
        isOpen={showPreviewModal}
        title="Form Preview"
        onClose={() => setShowPreviewModal(false)}
        showConfirm={false}
        maxWidth="md"
        fullWidth
      >
        {isPreviewLoading ? (
          <Box sx={{ textAlign: 'center', py: '2rem' }}>Loading preview...</Box>
        ) : previewData ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Typography variant="h4" fontWeight={600} fontSize="1.5rem">
              {previewData.title}
            </Typography>

            {previewData.introduction && (
              <Box>
                <Typography fontSize="0.875rem" color="#6b7280" bold>
                  Introduction
                </Typography>
                <Typography
                  fontSize="1rem"
                  color="#374151"
                  style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}
                >
                  {previewData.introduction}
                </Typography>
              </Box>
            )}

            {previewData.conditions && (
              <Box
                sx={{
                  background: '#f9fafb',
                  borderRadius: '0.5rem',
                  p: '16px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <Typography fontSize="0.875rem" color="#6b7280" bold>
                  Terms and Conditions
                </Typography>
                <Typography
                  fontSize="0.875rem"
                  color="#374151"
                  style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}
                >
                  {previewData.conditions}
                </Typography>
              </Box>
            )}

            {previewData.termsEnabled &&
              previewData.linkTitle &&
              previewData.linkUrl && (
                <Box>
                  <Typography fontSize="0.875rem" color="#6b7280" bold>
                    Terms Link
                  </Typography>
                  <a
                    href={previewData.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#3b82f6',
                      fontSize: '0.875rem',
                      marginTop: '4px',
                      display: 'inline-block',
                    }}
                  >
                    {previewData.linkTitle}
                  </a>
                </Box>
              )}

            {previewData.signatureEnabled && (
              <SignatureLine>Patient Signature</SignatureLine>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: '2rem', color: '#6b7280' }}>
            No preview data available.
          </Box>
        )}
      </Modal>
    </SettingsPage>
  )
}

export default PatientDocuments
